import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'dashboardPreferences';

export const TileSize = {
    COMPACT: 'compact',
    NORMAL: 'normal',
    EXPANDED: 'expanded',
};

const defaultPreferences = {
    tileOrder: [], // Array of zone names in order
    hiddenZones: [], // Array of zone names to hide
    tileSize: TileSize.NORMAL,
    tileColors: {}, // Object mapping zone names to colors
};

const DashboardPreferencesContext = createContext(null);

export function DashboardPreferencesProvider({ children }) {
    const [preferences, setPreferences] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...defaultPreferences, ...JSON.parse(saved) } : defaultPreferences;
        } catch {
            return defaultPreferences;
        }
    });

    const [isEditMode, setIsEditMode] = useState(false);

    // Save to localStorage whenever preferences change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }, [preferences]);

    const setTileOrder = useCallback((order) => {
        setPreferences(prev => ({ ...prev, tileOrder: order }));
    }, []);

    const setTileSize = useCallback((size) => {
        setPreferences(prev => ({ ...prev, tileSize: size }));
    }, []);

    const toggleZoneVisibility = useCallback((zone) => {
        setPreferences(prev => {
            const zoneKey = zone || 'null';
            const isHidden = prev.hiddenZones.includes(zoneKey);
            return {
                ...prev,
                hiddenZones: isHidden
                    ? prev.hiddenZones.filter(z => z !== zoneKey)
                    : [...prev.hiddenZones, zoneKey],
            };
        });
    }, []);

    const isZoneHidden = useCallback((zone) => {
        const zoneKey = zone || 'null';
        return preferences.hiddenZones.includes(zoneKey);
    }, [preferences.hiddenZones]);

    const showAllZones = useCallback(() => {
        setPreferences(prev => ({ ...prev, hiddenZones: [] }));
    }, []);

    const resetPreferences = useCallback(() => {
        setPreferences(defaultPreferences);
    }, []);

    const toggleEditMode = useCallback(() => {
        setIsEditMode(prev => !prev);
    }, []);

    const setTileColor = useCallback((zone, color) => {
        setPreferences(prev => ({
            ...prev,
            tileColors: {
                ...prev.tileColors,
                [zone || 'null']: color,
            },
        }));
    }, []);

    const getTileColor = useCallback((zone) => {
        const zoneKey = zone || 'null';
        return preferences.tileColors[zoneKey] || '#ffffff';
    }, [preferences.tileColors]);

    // Sort sensor data based on saved order and hidden zones
    const applySortAndFilter = useCallback((sensorData, language) => {
        // Filter out hidden zones
        const filtered = sensorData.filter(item => {
            const zoneKey = item.zone || 'null';
            return !preferences.hiddenZones.includes(zoneKey);
        });

        // Sort based on saved order
        if (preferences.tileOrder.length > 0) {
            return [...filtered].sort((a, b) => {
                const aKey = a.zone || 'null';
                const bKey = b.zone || 'null';
                const aIndex = preferences.tileOrder.indexOf(aKey);
                const bIndex = preferences.tileOrder.indexOf(bKey);

                // Items not in order go to the end
                if (aIndex === -1 && bIndex === -1) {
                    // Both not in order, sort alphabetically with null first
                    if (a.zone === null) return -1;
                    if (b.zone === null) return 1;
                    return a.zone.localeCompare(b.zone, language);
                }
                if (aIndex === -1) return 1;
                if (bIndex === -1) return -1;

                return aIndex - bIndex;
            });
        }

        // Default sort: null first, then alphabetically
        return [...filtered].sort((a, b) => {
            if (a.zone === null) return -1;
            if (b.zone === null) return 1;
            return a.zone.localeCompare(b.zone, language);
        });
    }, [preferences.tileOrder, preferences.hiddenZones]);

    const value = {
        preferences,
        tileSize: preferences.tileSize,
        hiddenZones: preferences.hiddenZones,
        tileOrder: preferences.tileOrder,
        isEditMode,
        setTileOrder,
        setTileSize,
        toggleZoneVisibility,
        isZoneHidden,
        showAllZones,
        resetPreferences,
        toggleEditMode,
        applySortAndFilter,
        setTileColor,
        getTileColor,
    };

    return (
        <DashboardPreferencesContext.Provider value={value}>
            {children}
        </DashboardPreferencesContext.Provider>
    );
}

export function useDashboardPreferences() {
    const context = useContext(DashboardPreferencesContext);
    if (!context) {
        throw new Error('useDashboardPreferences must be used within a DashboardPreferencesProvider');
    }
    return context;
}
